import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import StatusPill from "./StatusPill";
import { Link2, ExternalLink, Check } from "lucide-react";

function CodeBlock({ label, value, testid }) {
    if (value === null || value === undefined) return null;
    const txt =
        typeof value === "string" ? value : JSON.stringify(value, null, 2);
    return (
        <div className="mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] mb-2">
                {label}
            </div>
            <pre
                data-testid={testid}
                className="bg-[#090a0b] border border-[#262a2e] p-3 font-mono text-[11px] leading-relaxed text-[#f3f4f6] overflow-auto max-h-64 whitespace-pre-wrap break-words"
            >
                {txt}
            </pre>
        </div>
    );
}

export default function DetailDrawer({ action, open, onOpenChange }) {
    const [copied, setCopied] = React.useState(false);
    if (!action) return null;

    const base =
        (typeof window !== "undefined" && window.location && window.location.origin) ||
        process.env.REACT_APP_BACKEND_URL ||
        "";
    const receiptPath = `/r/${action.id}`;
    const receiptUrl = `${base}${receiptPath}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(receiptUrl);
        } catch {
            // noop
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!bg-[#111315] !border-l !border-[#262a2e] !p-0 !rounded-none w-full sm:!max-w-[520px] overflow-y-auto"
                data-testid="action-detail-drawer"
            >
                <SheetHeader className="p-6 border-b border-[#262a2e]">
                    <div className="flex items-center gap-3 mb-2">
                        <StatusPill status={action.status} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                            {action.type}
                        </span>
                    </div>
                    <SheetTitle className="font-sans text-lg font-semibold text-[#f3f4f6] text-left">
                        {action.target || "(no target)"}
                    </SheetTitle>
                    <SheetDescription asChild>
                        <div className="font-mono text-[10px] text-[#9ca3af] text-left">
                            id: {action.id}
                            <br />
                            workflow: {action.workflow_name || "—"}
                            <br />
                            created: {action.created_at}
                        </div>
                    </SheetDescription>
                </SheetHeader>

                <div className="p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <button
                            onClick={copy}
                            data-testid="detail-copy-receipt"
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-3 py-1.5 rounded-[2px]"
                        >
                            {copied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                            {copied ? "copied" : "copy receipt link"}
                        </button>
                        <a
                            href={receiptPath}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="detail-open-receipt"
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-3 py-1.5 rounded-[2px]"
                        >
                            <ExternalLink className="w-3 h-3" />
                            open receipt
                        </a>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-5">
                        <Metric label="Provider" value={action.provider || "—"} />
                        <Metric label="Retries" value={action.retry_count ?? 0} />
                        <Metric label="Attempts" value={action.attempts?.length ?? 0} />
                    </div>

                    {action.draft && (
                        <CodeBlock
                            label="DRAFT"
                            value={`Subject: ${action.draft.subject || ""}\n\n${action.draft.body || ""}`}
                            testid="detail-draft"
                        />
                    )}

                    <CodeBlock label="PAYLOAD" value={action.payload} testid="detail-payload" />
                    <CodeBlock label="RESPONSE" value={action.response} testid="detail-response" />
                    {action.error && (
                        <CodeBlock label="ERROR" value={action.error} testid="detail-error" />
                    )}

                    <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                        RETRY HISTORY ({action.attempts?.length ?? 0})
                    </div>
                    <div className="border border-[#262a2e] bg-[#090a0b]" data-testid="detail-attempts">
                        {(action.attempts || []).length === 0 && (
                            <div className="p-3 font-mono text-[11px] text-[#9ca3af]">
                                NO_ATTEMPTS_YET
                            </div>
                        )}
                        {(action.attempts || []).map((a, i) => (
                            <div
                                key={i}
                                className="px-3 py-2 border-b last:border-b-0 border-[#262a2e] font-mono text-[11px] flex items-start gap-3"
                            >
                                <span
                                    className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full"
                                    style={{ background: a.ok ? "#7cc93c" : "#ff453a" }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-[#9ca3af]">
                                        <span className="text-[#f3f4f6]">{a.provider}</span>
                                        <span>·</span>
                                        <span>{a.at?.slice(11, 19)}Z</span>
                                        <span>·</span>
                                        <span style={{ color: a.ok ? "#7cc93c" : "#ff453a" }}>
                                            {a.ok ? "OK" : "FAIL"}
                                        </span>
                                    </div>
                                    {a.error && (
                                        <div className="text-[#ff453a] mt-1 break-words">
                                            {a.error}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function Metric({ label, value }) {
    return (
        <div className="border border-[#262a2e] p-3 bg-[#090a0b]">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                {label}
            </div>
            <div className="font-mono text-sm text-[#f3f4f6] truncate">{value}</div>
        </div>
    );
}
